import { gql, useMutation, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import { DateTime } from '@components/misc/dateTime/dateTime';
import { NoPermissionAlert } from '@components/misc/noPermissionAlert';
import StateChangesModal from '@components/modals/stateChangesModal';
import {
  Chip,
  Link as MuiLink,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { Link } from '@ovComponents/2-component/orgLink/orgLink';
import { usePermission } from '@providers/userContextProvider';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { STATE_CHANGES_FIELD } from '@utils/reusableGraphQLFields';
import { usePageState } from '@utils/usePageState';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from 'hephaestus/web';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BulkTradeRunStates } from '../bulkTrader/bulkTradeRunStates';

const FETCH_TRADES = ({
  canReadClientLowRiskPii,
}: {
  canReadClientLowRiskPii: boolean;
}) => gql`
  query fetchTradeRequests($input: FetchTradeRequestsInput!) {
    fetchTradeRequests(input: $input) {
      tradeRequests {
        id
        actualQuantity
        actualTradePriceCents
        expectedQuantity
        expectedTradePriceCents
        state
        type
        currency
        moneyAllocatedCents
        ${STATE_CHANGES_FIELD}
        settlementDate
        account {
          type
          user {
            id
            ${canReadClientLowRiskPii ? 'firstName' : ''}
            ${canReadClientLowRiskPii ? 'lastName' : ''}
            ${canReadClientLowRiskPii ? 'entityName' : ''}
          }
        }
        financialProduct {
          ticker
          translatedName { ${TRANSLATED_STRING_FIELDS} }
        }
        createdAt
        hasPairs
      }
      totalCount
    }
  }
`;

const REMOVE_TRADE_REQUEST = gql`
  mutation removeBulkTrade($tradeRequestId: ObjectID!) {
    removeBulkTrade(tradeRequestId: $tradeRequestId) {
      success
    }
  }
`;

const TradeRequestsTable = ({
  showClient = false,
  showExpected = false,
  filter,
  bulkTradeRunState,
}: {
  showClient?: boolean;
  showExpected?: boolean;
  filter: any;
  bulkTradeRunState?: string;
  onTransition?: () => void;
}) => {
  const { t } = useTranslation(['components']);
  const canReadClientLowRiskPii = usePermission('read:client_low_risk_pii');
  const canReadTradeRequest = usePermission('read:trade_request');
  const [page, setPage] = usePageState(1, 'tra/page');
  const [state, setState] = usePageState('any', 'tra/state');
  const [activeTrade, setActiveTrade] = useState<any>({});
  const [modalOpen, setModalOpen] = useState(false);
  const pageSize = 15;

  const { loading, data, previousData, refetch } = useQuery(
    FETCH_TRADES({ canReadClientLowRiskPii }),
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        input: {
          filter: {
            ...filter,
            tradeState: state === 'any' ? undefined : state,
          },
          pagination: {
            sortField: 'account',
            sortDesc: false,
            perPage: pageSize,
            offSet: (page - 1) * pageSize,
          },
        },
      },
    },
  );

  const [removeTradeRequest, { loading: cancelLoading }] = useMutation(
    REMOVE_TRADE_REQUEST,
    {
      onCompleted: () => {
        refetch();
        setModalOpen(false);
      },
    },
  );

  const stateColor = (forState: any) =>
    forState === 'RECONCILED'
      ? 'success'
      : ['FAILED', 'CANCELED'].includes(forState)
        ? 'error'
        : 'warning';

  useEffect(
    () =>
      function cleanupOnUnmount() {
        setState('any');
        setPage(1);
      },
    [],
  );

  useEffect(() => {
    if (bulkTradeRunState === BulkTradeRunStates.COMPLETED) {
      refetch();
    }
  }, [bulkTradeRunState, refetch]);

  if (!canReadTradeRequest)
    return <NoPermissionAlert missing='read:trade_request' />;

  return (
    <Box>
      {loading && !previousData ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress sx={{ m: 18 }} />
        </Box>
      ) : (
        <>
          <Grid container sx={{ overflow: 'hidden' }}>
            <Grid sx={{ overflow: 'auto' }} size={12}>
              <Table aria-label='table'>
                <TableHead>
                  <TableRow>
                    {showClient && (
                      <TableCell>
                        <Typography variant='labelSmall'>
                          {t('tradeTable.client')}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant='labelSmall'>
                        {t('tradeTable.account')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='labelSmall'>
                        {t('tradeTable.security')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='labelSmall'>
                        {t('tradeTable.type')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='labelSmall'>
                        {t('tradeTable.currency')}
                      </Typography>
                    </TableCell>
                    {showExpected ? (
                      <>
                        <TableCell>
                          <Typography variant='labelSmall'>
                            {t('tradeTable.expectedQuantity')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='labelSmall'>
                            {t('tradeTable.expectedPrice')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='labelSmall'>
                            {t('tradeTable.expectedTotalValue')}
                          </Typography>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>
                          <Typography variant='labelSmall'>
                            {t('tradeTable.quantity')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='labelSmall'>
                            {t('tradeTable.price')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='labelSmall'>
                            {t('tradeTable.totalValue')}
                          </Typography>
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      <Typography variant='labelSmall'>
                        {t('tradeTable.state')}
                      </Typography>
                    </TableCell>
                    {bulkTradeRunState === 'COMPLETED' && (
                      <>
                        <TableCell>
                          <Typography variant='labelSmall'>
                            {t('tradeTable.tradeDate')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='labelSmall'>
                            {t('tradeTable.settlementDate')}
                          </Typography>
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      <Typography variant='labelSmall'>
                        {t('tradeTable.createdAt')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(
                    data || previousData
                  )?.fetchTradeRequests?.tradeRequests?.map((trade: any) => (
                    <TableRow
                      hover
                      onClick={() => {
                        setActiveTrade(trade);
                        setModalOpen(true);
                      }}
                      key={trade.id}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        textDecoration: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {showClient && (
                        <TableCell component='th' scope='row'>
                          <MuiLink
                            component={Link}
                            to={`/clients/${trade.account.user.id}`}
                          >
                            {trade.account.user.entityName ||
                              [
                                trade.account.user.firstName,
                                trade.account.user.lastName,
                              ]
                                .filter(Boolean)
                                .join(' ')}
                          </MuiLink>
                        </TableCell>
                      )}
                      <TableCell component='th' scope='row'>
                        {t(`accountTypes:${trade.account.type}`)}
                      </TableCell>
                      <TableCell component='th' scope='row'>
                        {trade.financialProduct.ticker}
                      </TableCell>
                      <TableCell>
                        <Chip size='small' label={trade.type} />
                      </TableCell>
                      <TableCell>{trade.currency}</TableCell>
                      {showExpected || trade.state === 'INITIATED' ? (
                        <>
                          <TableCell>{trade.expectedQuantity}</TableCell>
                          <TableCell>
                            {formatMoneyValue(trade.expectedTradePriceCents)}
                          </TableCell>
                          <TableCell>
                            {formatMoneyValue(trade.moneyAllocatedCents)}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{trade.actualQuantity}</TableCell>
                          <TableCell>
                            {formatMoneyValue(trade.actualTradePriceCents)}
                          </TableCell>
                          <TableCell>
                            {formatMoneyValue(
                              trade.actualTradePriceCents *
                                trade.actualQuantity,
                            )}
                          </TableCell>
                        </>
                      )}

                      <TableCell>
                        <Chip
                          size='small'
                          label={trade.state}
                          color={stateColor(trade.state)}
                        />
                      </TableCell>
                      {bulkTradeRunState === 'COMPLETED' && (
                        <>
                          <TableCell>
                            {trade.createdAt
                              ? dayjs(trade.createdAt)
                                  .utc()
                                  .format('YYYY-MM-DD')
                              : 'N/A'}
                          </TableCell>
                          <TableCell>{trade.settlementDate || 'N/A'}</TableCell>
                        </>
                      )}
                      <TableCell>
                        <DateTime date={trade.createdAt} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Grid>
            <Pagination
              count={Math.ceil(
                ((data || previousData)?.fetchTradeRequests?.totalCount ?? 0) /
                  pageSize,
              )}
              page={page}
              onChange={(_e, newPage) => setPage(newPage)}
              sx={{
                p: 1,
                textAlign: 'right',
                '.MuiPagination-ul': {
                  justifyContent: 'end',
                },
              }}
            />
          </Grid>
          <StateChangesModal
            open={modalOpen}
            handleClose={() => setModalOpen(false)}
            stateChanges={[
              { to: 'INITIATED', createdAt: activeTrade.createdAt },
              ...(activeTrade?.stateMachineChanges || []),
            ]}
            title={`${activeTrade.type} - ${formatMoneyValue(activeTrade.actualTradePriceCents * activeTrade.actualQuantity)} - ${translateBackend(activeTrade?.financialProduct?.translatedName)}`}
            id={activeTrade.id}
          >
            {activeTrade.state === 'INITIATED' && !activeTrade.hasPairs && (
              <Button
                variant='outlined'
                color='error'
                disabled={cancelLoading}
                onClick={() => {
                  removeTradeRequest({
                    variables: {
                      tradeRequestId: activeTrade.id,
                    },
                  });
                }}
              >
                {t('tradeTable.remove')}
              </Button>
            )}
          </StateChangesModal>
        </>
      )}
    </Box>
  );
};

export default TradeRequestsTable;
